import { AnyAction } from 'redux'
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { defaultFlow, multiResponseFlow } from 'redux/config/saga/Common'
import { employeeActions } from 'redux/employee/employeeSlice'
import EmployeeService from 'redux/employee/employeeService'
import {
  ChangeWorksitePayload,
  EmployeeIdParams,
  EmployeeSuccessPayload,
  MyCommuterDetailsParams,
  MyCommutersParams,
  MyCommuterTransactionsParams,
  CreateFleetCardPayload,
  ReplaceFleetCardPayload,
  RequestTransitCardPayload,
  ToggleFleetCardPayload,
} from 'redux/employee/employeeTypes'
import { employerActions } from 'redux/employer/employerSlice'
import { AppState } from 'redux/config/store'
import { RosterListParams } from './rosterQuery'
import { userActions } from 'redux/user/userSlice'
import { Action } from 'shared/Types/Actions'

export function* getAllEmployees(action: AnyAction) {
  yield defaultFlow(
    action,
    EmployeeService.getAllEmployees,
    employeeActions.getEmployeesSuccess
  )
}

export function* updateEmployee(action: Action<EmployeeSuccessPayload>) {
  yield defaultFlow(
    action,
    EmployeeService.updateEmployee,
    employeeActions.updateEmployeeSuccess
  )
}

export function* deleteEmployee(action: Action<string>) {
  yield defaultFlow(
    action,
    EmployeeService.deleteEmployee,
    employeeActions.deleteEmployeeSuccess
  )
}

export function* createEmployee(action: Action<any>) {
  yield defaultFlow(
    action,
    EmployeeService.createEmployee,
    employeeActions.createEmployeeSuccess
  )
}

export function* inviteEmployees(action: Action<string[]>) {
  yield multiResponseFlow(
    action,
    EmployeeService.inviteEmployees,
    employeeActions.inviteEmployeesSuccess
  )
}

export function* getMyCommuters(action: Action<MyCommutersParams>) {
  yield defaultFlow(
    action,
    EmployeeService.getMyCommuters,
    employeeActions.getMyCommutersSuccess
  )
}

export function* getCommuters(action: Action<string>) {
  yield defaultFlow(
    action,
    EmployeeService.getCommuters,
    employeeActions.getCommutersSuccess
  )
}

export function* getMyCommuterDetails(action: Action<MyCommuterDetailsParams>) {
  yield defaultFlow(
    action,
    EmployeeService.getMyCommuterDetails,
    employeeActions.getMyCommuterDetailsSuccess
  )
}

export function* getMyCommuterTransactions(
  action: Action<MyCommuterTransactionsParams>
) {
  yield defaultFlow(
    action,
    EmployeeService.getMyCommuterTransactions,
    employeeActions.getMyCommuterTransactionsSuccess
  )
}

export function* resendEmployeeInvite(action: Action<string>) {
  yield defaultFlow(
    action,
    EmployeeService.resendEmployeeInvite,
    employeeActions.resendEmployeeInviteSuccess
  )
}

export function* getArchivedEmployees(action: AnyAction) {
  yield defaultFlow(
    action,
    EmployeeService.getArchivedEmployees,
    employeeActions.getArchivedEmployeesSuccess
  )
}

export function* archiveEmployee(action: Action<EmployeeIdParams>) {
  yield defaultFlow(
    action,
    EmployeeService.archiveEmployee,
    employeeActions.archiveEmployeeSuccess
  )
}

export function* reactivateEmployee(action: Action<EmployeeIdParams>) {
  yield defaultFlow(
    action,
    EmployeeService.reactivateEmployee,
    employeeActions.reactivateEmployeeSuccess
  )
}

export function* getRosterPage(action: Action<RosterListParams>) {
  try {
    yield put(employeeActions.rosterRequestStarted(action.payload))
    const res = yield call(EmployeeService.getRosterPage, action.payload)
    yield put({
      type: employeeActions.getRosterPageSuccess.type,
      payload: res.data,
      meta: action.payload,
    })
  } catch (_error) {
    yield put(employeeActions.rosterRequestFailed())
  }
}

export function* getRosterSummary(action: Action<{ segment?: string }>) {
  yield defaultFlow(
    action,
    EmployeeService.getRosterSummary,
    employeeActions.getRosterSummarySuccess
  )
}

export function* refreshActiveRoster() {
  const query: RosterListParams | null = yield select(
    (state: AppState) => state.employee.activeRosterQuery
  )
  if (!query) {
    return
  }
  yield put(employeeActions.getRosterPage(query))
  yield put(employeeActions.getRosterSummary({ segment: query.segment }))
}

export function* getDeclinedEmployees(action: AnyAction) {
  yield defaultFlow(
    action,
    EmployeeService.getDeclinedEmployees,
    employeeActions.getDeclinedEmployeesSuccess
  )
}

export function* changeEmployeeWorksite(action: Action<ChangeWorksitePayload>) {
  yield defaultFlow(
    action,
    EmployeeService.changeEmployeeWorksite,
    employeeActions.changeEmployeeWorksiteSuccess
  )
}

/**************************************
 * Watchers
 **************************************/

export function* watchGetAllEmployees() {
  yield takeLatest(
    [
      employeeActions.getEmployees.type,
      userActions.getUserSuccess.type,
      employerActions.bulkSyncFinchWorksiteEmployeesSuccess.type,
    ],
    getAllEmployees
  )
}

export function* watchUpdateEmployee() {
  yield takeLatest([employeeActions.updateEmployee.type], updateEmployee)
}

export function* watchDeleteEmployee() {
  yield takeLatest([employeeActions.deleteEmployee.type], deleteEmployee)
}

export function* watchCreateEmployee() {
  yield takeLatest([employeeActions.createEmployee.type], createEmployee)
}

export function* watchInviteEmployees() {
  yield takeLatest([employeeActions.inviteEmployees.type], inviteEmployees)
}

export function* watchGetCommuters() {
  yield takeEvery([employeeActions.getCommuters.type], getCommuters)
}

export function* watchGetMyCommuters() {
  yield takeEvery(
    [
      employeeActions.getMyCommuters.type,
      employerActions.uploadFileSuccess.type,
      employerActions.unarchiveWorksiteSuccess.type,
    ],
    getMyCommuters
  )
}

export function* watchGetMyCommuterDetails() {
  yield takeEvery(
    [employeeActions.getMyCommuterDetails.type],
    getMyCommuterDetails
  )
}

export function* watchGetMyCommuterTransactions() {
  yield takeEvery(
    [employeeActions.getMyCommuterTransactions.type],
    getMyCommuterTransactions
  )
}

export function* watchResendEmployeeInvite() {
  yield takeEvery(
    [employeeActions.resendEmployeeInvite.type],
    resendEmployeeInvite
  )
}

export function* watchGetArchivedEmployees() {
  yield takeEvery(
    [employeeActions.getArchivedEmployees.type],
    getArchivedEmployees
  )
}

export function* watchArchiveEmployee() {
  yield takeEvery([employeeActions.archiveEmployee.type], archiveEmployee)
}

export function* watchReactivateEmployee() {
  yield takeEvery([employeeActions.reactivateEmployee.type], reactivateEmployee)
}

export function* watchGetDeclineEmployees() {
  yield takeEvery(
    [employeeActions.getDeclinedEmployees.type],
    getDeclinedEmployees
  )
}

export function* watchGetRosterPage() {
  yield takeLatest([employeeActions.getRosterPage.type], getRosterPage)
}

export function* watchGetRosterSummary() {
  yield takeLatest([employeeActions.getRosterSummary.type], getRosterSummary)
}

export function* watchRefreshActiveRoster() {
  yield takeLatest(
    [
      employeeActions.refreshActiveRoster.type,
      employeeActions.archiveEmployeeSuccess.type,
      employeeActions.reactivateEmployeeSuccess.type,
      employerActions.declineInactiveEmployeesSuccess.type,
      employerActions.forceActivateInactiveEmployeesSuccess.type,
      employerActions.uploadFileSuccess.type,
      employerActions.unarchiveWorksiteSuccess.type,
    ],
    refreshActiveRoster
  )
}

export function* watchChangeEmployeeWorksite() {
  yield takeEvery(
    [employeeActions.changeEmployeeWorksite.type],
    changeEmployeeWorksite
  )
}

export function* requestTransitCard(action: Action<RequestTransitCardPayload>) {
  yield defaultFlow(
    action,
    EmployeeService.requestTransitCard,
    employeeActions.requestTransitCardSuccess
  )
}

export function* watchRequestTransitCard() {
  yield takeEvery(
    [employeeActions.requestTransitCard.type],
    requestTransitCard
  )
}

export function* activateFleetCard(action: Action<ToggleFleetCardPayload>) {
  yield defaultFlow(
    action,
    EmployeeService.activateFleetCard,
    employeeActions.activateFleetCardSuccess
  )
}

export function* deactivateFleetCard(action: Action<ToggleFleetCardPayload>) {
  yield defaultFlow(
    action,
    EmployeeService.deactivateFleetCard,
    employeeActions.deactivateFleetCardSuccess
  )
}

export function* replaceFleetCard(action: Action<ReplaceFleetCardPayload>) {
  try {
    const res = yield call(EmployeeService.replaceFleetCard, action.payload)
    yield put({
      type: employeeActions.replaceFleetCardSuccess().type,
      payload: res.data,
      meta: action.payload,
    })
    const { employeeId, detailStartDate, detailEndDate } = action.payload
    if (detailStartDate && detailEndDate) {
      yield put(
        employeeActions.getMyCommuterDetails({
          id: employeeId,
          startDate: detailStartDate,
          endDate: detailEndDate,
        })
      )
    }
  } catch (error) {
    yield put({
      type: employeeActions.replaceFleetCardFailed().type,
    })
  }
}

export function* createFleetCard(action: Action<CreateFleetCardPayload>) {
  try {
    const res = yield call(EmployeeService.createFleetCard, action.payload)
    yield put({
      type: employeeActions.createFleetCardSuccess().type,
      payload: res.data,
      meta: action.payload,
    })
    const { employeeId, detailStartDate, detailEndDate } = action.payload
    if (detailStartDate && detailEndDate) {
      yield put(
        employeeActions.getMyCommuterDetails({
          id: employeeId,
          startDate: detailStartDate,
          endDate: detailEndDate,
        })
      )
    }
  } catch (error) {
    yield put({
      type: employeeActions.createFleetCardFailed().type,
    })
  }
}

export function* watchActivateFleetCard() {
  yield takeEvery(
    [employeeActions.activateFleetCard.type],
    activateFleetCard
  )
}

export function* watchDeactivateFleetCard() {
  yield takeEvery(
    [employeeActions.deactivateFleetCard.type],
    deactivateFleetCard
  )
}

export function* watchReplaceFleetCard() {
  yield takeEvery([employeeActions.replaceFleetCard.type], replaceFleetCard)
}

export function* watchCreateFleetCard() {
  yield takeEvery([employeeActions.createFleetCard.type], createFleetCard)
}

export default function* () {
  yield all([
    watchGetAllEmployees(),
    watchUpdateEmployee(),
    watchDeleteEmployee(),
    watchCreateEmployee(),
    watchInviteEmployees(),
    watchGetCommuters(),
    watchGetMyCommuters(),
    watchGetMyCommuterDetails(),
    watchGetMyCommuterTransactions(),
    watchResendEmployeeInvite(),
    watchGetArchivedEmployees(),
    watchArchiveEmployee(),
    watchReactivateEmployee(),
    watchGetDeclineEmployees(),
    watchGetRosterPage(),
    watchGetRosterSummary(),
    watchRefreshActiveRoster(),
    watchChangeEmployeeWorksite(),
    watchRequestTransitCard(),
    watchActivateFleetCard(),
    watchDeactivateFleetCard(),
    watchReplaceFleetCard(),
    watchCreateFleetCard(),
  ])
}
